// client/src/services/pushService.ts (converted from .js, ST-00229)
//
// Web Push for the signed-in user: the browser subscription (service worker
// PushManager) and its registration with the server (/notifications/push/*).
//
// Re-sync (ST-00229, audit RPT-3): the server used to store every subscription under
// the user 'anonymous', so no push reached its user; migration 055 switches those
// rows off. A browser that already holds a subscription re-registers it under the
// signed-in user the first time a page checks the push state (once per page load;
// the server's subscribe is an upsert), so "enabled" in Settings is true again.
import apiClient from './apiService';
import { getRegistration } from '../serviceWorkerRegistration';

interface PushStatus {
  isSubscribed: boolean;
  subscriptionCount: number;
}

/** The registration surface used here (getRegistration is untyped JS). */
interface PushRegistration {
  pushManager: PushManager;
}

const registration = (): Promise<PushRegistration> =>
  getRegistration() as Promise<PushRegistration>;

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Posts a browser subscription to the server under the signed-in user. */
async function registerWithServer(subscription: PushSubscription): Promise<void> {
  await apiClient.post('/notifications/push/subscribe', {
    subscription: subscription.toJSON(),
    userAgent: navigator.userAgent,
  });
}

let resynced = false;

export function isPushSupported(): boolean {
  return 'PushManager' in window && 'serviceWorker' in navigator;
}

export async function getVapidKey(): Promise<string> {
  const res = await apiClient.get('/notifications/push/vapid-key');
  return (res.data as { publicKey: string }).publicKey;
}

export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const reg = await registration();
    const sub = await reg.pushManager.getSubscription();
    if (sub && !resynced) {
      resynced = true;
      // Fire and forget: the answer is the browser's state either way.
      registerWithServer(sub).catch(() => {
        resynced = false;
      });
    }
    return !!sub;
  } catch {
    return false;
  }
}

export async function subscribeToPush(): Promise<PushSubscription> {
  if (!isPushSupported()) throw new Error('Push notifications not supported');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission denied');

  const vapidKey = await getVapidKey();
  const reg = await registration();

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });

  await registerWithServer(subscription);
  resynced = true;

  return subscription;
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return;

  const reg = await registration();
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return;

  await apiClient.post('/notifications/push/unsubscribe', {
    endpoint: subscription.endpoint,
  });

  await subscription.unsubscribe();
}

export async function sendTestPush(): Promise<void> {
  await apiClient.post('/notifications/push/test');
}

export async function getPushStatus(): Promise<PushStatus> {
  const res = await apiClient.get('/notifications/push/status');
  return res.data as PushStatus;
}
